import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { Hero } from "@/components/rawan/Hero";
import { PosterRow } from "@/components/rawan/PosterRow";
import { LazyMount } from "@/components/rawan/LazyMount";
import { AdSlot } from "@/components/rawan/AdSlot";
import { useAllItems } from "@/lib/library-store";
import { useTheme } from "@/lib/theme-context";

// Below-the-fold sections: split out of the home chunk so they don't block LCP.
const StoriesRow = lazy(() => import("@/components/rawan/StoriesRow").then((m) => ({ default: m.StoriesRow })));
const CategoriesSection = lazy(() => import("@/components/rawan/CategoriesSection").then((m) => ({ default: m.CategoriesSection })));
const NewsCard = lazy(() => import("@/components/rawan/NewsCard").then((m) => ({ default: m.NewsCard })));


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Rawan UI — أفلام و مسلسلات بأعلى جودة" },
      { name: "description", content: "شاهد وحمّل أحدث الأفلام والمسلسلات بجودات متعددة 4K, 1080p, 720p — قالب Rawan UI الرسمي." },
      { property: "og:title", content: "Rawan UI — أفلام و مسلسلات" },
      { property: "og:description", content: "أحدث الأفلام والمسلسلات بأعلى جودة." },
      { property: "og:url", content: "https://rawan.elhizazi.me/" },
    ],
    links: [
      { rel: "canonical", href: "https://rawan.elhizazi.me/" },
      // Warm up the image origins used by the LCP hero backdrop.
      { rel: "preconnect", href: "https://image.tmdb.org", crossOrigin: "" },
      { rel: "preconnect", href: "https://fastly.picsum.photos", crossOrigin: "" },
      { rel: "dns-prefetch", href: "https://image.tmdb.org" },
      { rel: "dns-prefetch", href: "https://fastly.picsum.photos" },
      // Preload the LCP hero backdrop. The first trending item is fixed
      // (inception-2010) so we can hint the browser to fetch it before the
      // JS bundle parses. imagesrcset lets phones grab the small variant
      // and desktops grab the medium one without a second request.
      {
        rel: "preload",
        as: "image",
        href: "https://image.tmdb.org/t/p/w500/s3TBrRGB1iav7gFOCNx3H31MoES.jpg",
        imageSrcSet:
          "https://image.tmdb.org/t/p/w300/s3TBrRGB1iav7gFOCNx3H31MoES.jpg 300w, https://image.tmdb.org/t/p/w500/s3TBrRGB1iav7gFOCNx3H31MoES.jpg 500w, https://image.tmdb.org/t/p/w780/s3TBrRGB1iav7gFOCNx3H31MoES.jpg 780w, https://image.tmdb.org/t/p/w1280/s3TBrRGB1iav7gFOCNx3H31MoES.jpg 1280w",
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Rawan UI — أفلام و مسلسلات",
          description: "أحدث الأفلام والمسلسلات بأعلى جودة.",
          inLanguage: ["ar", "en"],
        }),
      },
    ],
  }),
  component: Home,
});

function Home() {
  const { dir, language } = useTheme();
  const isAr = dir === "rtl";
  const t = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);
  const items = useAllItems();
  const trending = items.filter((m) => m.isTrending);
  const fresh = items.filter((m) => m.isNew);
  const movies = items.filter((m) => m.type === "movie");
  const series = items.filter((m) => m.type === "series");
  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1">
        <Hero />
        <div className="container mx-auto px-4"><AdSlot slot="home_top" format="horizontal" /></div>
        <LazyMount minHeight={200}>
          <Suspense fallback={null}><StoriesRow /></Suspense>
        </LazyMount>
        {/* Reserve space so the first PosterRow doesn't push subsequent
            sections when its data (or lazy StoriesRow above) arrives. */}
        <div style={{ minHeight: 400 }}>
          <PosterRow
            title={t("الأكثر رواجاً الآن", "Trending Now", "Tendances du moment")}
            subtitle={t("الأكثر مشاهدة هذا الأسبوع", "Most watched this week", "Les plus regardés cette semaine")}
            movies={trending}
          />
        </div>


        <LazyMount minHeight={400}>
          <PosterRow
            title={t("أُضيف حديثاً", "New Releases", "Nouveautés")}
            subtitle={t("أحدث ما تمت إضافته", "Latest additions", "Derniers ajouts")}
            movies={fresh}
          />
        </LazyMount>
        <LazyMount minHeight={400}>
          <PosterRow
            title={t("\u0623\u0641\u0644\u0627\u0645", "Movies", "Films")}
            subtitle={t("مجموعة مختارة من أفضل الأفلام", "Hand-picked top movies")}
            movies={movies}
          />
        </LazyMount>
        <LazyMount minHeight={400}>
          <PosterRow
            title={t("\u0645\u0633\u0644\u0633\u0644\u0627\u062a", "TV Series", "S\u00e9ries")}
            subtitle={t("أبرز المسلسلات", "Top series")}
            movies={series}
          />
        </LazyMount>
        <div className="container mx-auto px-4"><AdSlot slot="home_mid" format="horizontal" /></div>
        <LazyMount minHeight={300}>
          <Suspense fallback={null}><CategoriesSection /></Suspense>
        </LazyMount>
        <LazyMount minHeight={200}>
          <Suspense fallback={null}><NewsCard /></Suspense>
        </LazyMount>


        <div className="container mx-auto px-4"><AdSlot slot="home_bottom" format="horizontal" /></div>
      </main>
      <Footer />
    </div>
  );
}
